import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { AxiosInstance } from 'axios';

import { Card } from '../../components/atoms/Card';
import { Alert, PageHeadline } from '../../components/atoms';

import TitlePageInit from '../../components/atoms/TitlePageInit';
import CompanyInfoCard from '../../components/CompanyInfoCard';
import DynamicButtonGrid from '../../components/CompanyInfoCard/DynamicButton';

import { AccountService } from '../../services/AccountService';
import { EnumDocumentType, ICompanyDocument } from '../../services/Interfaces/IAccountService';
import { IShareholder } from '../../services/Interfaces/IManagemenntService';

import { useAuth } from '../../store/auth';
import { RootState } from '../../store';
import { useAppSelector } from '../../hooks/hooksRedux';

import { downloadFileS3 } from '../../utils/downloadFile';
import { getJurisdictionLabel, isDelawareJurisdiction } from '../../utils/jurisdiction';
import { useApi } from '../../services/hooks/apiHook';
import { TabsDocuments } from './TabsDocuments';
import { DashboardCustomerContext } from './Context';

const getDirectorsFromShareholders = (shareholders: IShareholder[]) => {
  return shareholders.filter((s) => s.is_director === 1);
};

const CompanyInfoScreen: React.FC = () => {
  const [documentCompany, setDocumentCompany] = useState<any>();
  const [fileDownload, setFileDownload] = useState<any>();

  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [isSigned, setIsSigned] = useState<boolean>(false);

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const { token, company } = useAuth();
  const api: AxiosInstance = useApi();
  const { companyInfo, refreshCompanyInfo } = useContext(DashboardCustomerContext);

  const typeDocument = EnumDocumentType.Certificate;

  const headers = useMemo(
    () => ({
      'Content-Language': legalPersonalitySlug,
      Authorization: token ? `Bearer ${token}` : ''
    }),
    [legalPersonalitySlug, token]
  );

  const fetchedDocumentsCompanyIdRef = useRef<number | string | undefined>();

  const fetchGetDocumentsCompany = useCallback(
    async (companyParam?: any, force = false) => {
      const targetCompany = companyParam ?? company;

      if (!targetCompany?.id) {
        return;
      }

      if (!force && fetchedDocumentsCompanyIdRef.current === targetCompany.id) {
        return;
      }

      fetchedDocumentsCompanyIdRef.current = targetCompany.id;

      try {
        const { data } = await AccountService.getCompaniesDocuments(api, targetCompany.id, typeDocument, { headers });

        setDocumentCompany(data);
        setShowMessage(false);
        return data;
      } catch (error) {
        console.error(error);
        fetchedDocumentsCompanyIdRef.current = undefined;
      }
    },
    [api, company, headers, typeDocument]
  );

  const fetchUrlTempDownloadCertificate = useCallback(async () => {
    try {
      if (documentCompany.length) {
        const { data } = await AccountService.getDownloadDocuments(api, documentCompany[0].id, { headers });
        setFileDownload(data);
        return data;
      } else {
        console.log('Error, url download is null', documentCompany);
      }
    } catch (error) {
      console.error(error);
    }
  }, [api, documentCompany, headers]);

  const handleDownload = async () => {
    const { temporaryUrl } = fileDownload;

    if (temporaryUrl) {
      const fileDownloadUrl = documentCompany.map((company: ICompanyDocument) => company.file);

      return downloadFileS3(temporaryUrl, fileDownloadUrl[0].file_name);
    }
  };

  const handleDownloadTransferDocument = async () => {
    const companyDocuments =
      (companyInfo?.company_documents ?? []).filter(
        (document: { type?: string }) => document?.type === 'certificate_incorporation'
      ) || [];
    if (companyDocuments?.length > 0) {
      const documentEntry = companyDocuments[0]?.document as { description: string; document_url: string };
      if (documentEntry) {
        const { description, document_url } = documentEntry;

        return downloadFileS3(description, document_url);
      }
    }
  };

  useEffect(() => {
    if (!company?.id) {
      setDocumentCompany(undefined);
      fetchedDocumentsCompanyIdRef.current = undefined;
      return;
    }

    if (!companyInfo && company?.id) {
      void refreshCompanyInfo();
    }

    void fetchGetDocumentsCompany(company);
  }, [company?.id, companyInfo, refreshCompanyInfo, fetchGetDocumentsCompany]);

  useEffect(() => {
    if (documentCompany) {
      setIsSigned(documentCompany[0]?.signed);

      void fetchUrlTempDownloadCertificate();
    }
  }, [documentCompany, fetchUrlTempDownloadCertificate]);

  if (!company) {
    setShowMessage(true);
    return null;
  }

  const directors = companyInfo?.shareholders ? getDirectorsFromShareholders(companyInfo?.shareholders) : [];
  const typeCompanyRequest = 'new_company';
  const isDelaware = isDelawareJurisdiction(companyInfo?.jurisdiction);
  const jurisdictionLabel = getJurisdictionLabel(companyInfo?.jurisdiction);

  const labelsCompanyInfo = [
    t('labels.lblRegistrationNo'),
    t('others.othPreferredName'),
    t('labels.lblFinancialYear'),
    t('labels.lblIncorporationDate'),
    t('labels.lblCompanyStatus')
  ];

  const labelsCapital = [
    t('labels.lblClassOfShares'),
    t('others.othTotalAuthorizedShares'),
    t('labels.lblTotalIssuedShareCapital'),
    t('others.othCurrency')
  ];

  const valuesCompanyInfo = [
    companyInfo?.registration_number,
    companyInfo?.name || companyInfo?.ibc_primary,
    companyInfo?.financial_year,
    companyInfo?.incorporation_date,
    companyInfo?.status !== 'approved' ? t('others.othWaitingApproval') : t('others.othApproved')
  ];

  const valuesCapital = [
    t('others.othOrdinary'),
    companyInfo?.total_authorized_shares,
    companyInfo?.total_issued_shares,
    companyInfo?.currency?.acronym
  ];

  const sicDescriptionsRaw = (companyInfo?.sics ?? []).map(
    (s: { sic_code?: { sic_section?: { description?: string } } }): string | undefined =>
      s?.sic_code?.sic_section?.description
  );

  const sicDescriptions: string[] = Array.from(
    new Set(
      sicDescriptionsRaw.filter(
        (description: string | undefined): description is string => Boolean(description)
      )
    )
  );
  const primaryShareholder = (companyInfo?.shareholders ?? []).find(
    (s: { is_director?: boolean | number | string }) => s?.is_director
  );
  return (
    <>
      <Alert
        typeIcon={'error'}
        message={'Company not found'}
        show={showMessage}
        onClose={() => setShowMessage(false)}
      />
      <div className="mb-6">
        <PageHeadline title={t('labels.lblCompanyDetails')}/>
      </div>
      {/* <TitlePageInit textTitle={t('labels.lblCompanyDetails')} />

      

      <Card.Root className="mt-4">
        <Card.Header className="font-medium">
          <h3 className="text-lg font-medium">{t('labels.lblCompanyInfo')}</h3>
        </Card.Header>

        <Card.Content className="justify-between">
          <CompanyInfoCard labels={labelsCompanyInfo} values={valuesCompanyInfo} />
        </Card.Content>
      </Card.Root>

      {directors.length ? (
        <Card.Root className="mt-4">
          <Card.Header className="font-medium">{t('others.othIndividualDirectors')}</Card.Header>
          <Card.Content className="justify-between">
            <div className="flex flex-1 flex-row items-center gap-6">
              <img src="/svg/director-asset.png" className="aspect-square h-16" alt="" />

              <div className="flex flex-1 flex-row items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#0C1FAA]">{directors?.[0].name}</h3>
                  <div>
                    <p className="text-base text-[#727272]">{directors?.[0].telefones?.[0]?.number}</p>
                    <p className="text-base text-[#727272]">{directors?.[0].email}</p>
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#0C1FAA]">{t('others.othRole')}</h3>
                  <ul className="list-disc text-base text-[#727272]">
                    <li>{t('others.othCompanyAdmin')}</li>
                    <li>{t('labels.lblShareholder')}</li>
                    <li>{t('labels.lblDirector')}</li>
                  </ul>
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#0C1FAA]">{t('others.othBeneficialOwner')}</h3>
                  <span className="text-base text-[#727272]">Fabio Almeida</span>
                </div>
              </div>
            </div>
          </Card.Content>
        </Card.Root>
      ) : undefined}

      <Card.Root className="mt-4">
        <Card.Header className="font-medium">Capital</Card.Header>
        <Card.Content className="justify-between">
          <CompanyInfoCard labels={labelsCapital} values={valuesCapital} />
        </Card.Content>
      </Card.Root>*/}



      <Card.Root className="mt-4 p-4 border rounded-md bg-white shadow-sm">
        <div className="border-b border-gray-200 pb-2 mb-3">
          <h1 className="break-words text-xl font-bold uppercase text-blue-900">
            {company.status === 'approved' ? (company.name || company.ibc_primary) : company.ibc_primary}
          </h1>
          <p className="mt-0.5 text-xs text-gray-600">
            <span className="block sm:inline">
              {t('labels.lblRegistrationNo')}: <strong>{company.registration_number != null ? company.registration_number : '---'}</strong>
            </span>
            <span className="hidden sm:inline">{' · '}</span>
            <span className="block sm:inline">
              {t('labels.lblStatus')}: <strong>{company.status === 'approved' ? t('others.othComplete') : t('others.othPending')}</strong>
            </span>
            {jurisdictionLabel && (
              <>
                <span className="hidden sm:inline">{' · '}</span>
                <span className="block sm:inline">
                  {t('labels.lblJurisdictions')}: <strong>{jurisdictionLabel}</strong>
                </span>
              </>
            )}
          </p>
        </div>

        {/* Indicador "Nome da empresa" só quando pendente (opções); quando completo fica só no grid */}
        {company.status !== 'approved' && (
          <p className="mb-2 text-xs font-medium text-gray-500">{t('others.othCompanyName')}</p>
        )}
        <div className="grid min-w-0 grid-cols-1 gap-4 text-sm [&>*]:min-w-0 sm:grid-cols-2 lg:grid-cols-3">
          {company.status === 'approved' ? (
            <>
              <div>
                <p className="text-gray-500">{t('others.othCompanyName')}</p>
                <p className="mt-0.5 break-words font-medium text-gray-900">{company.name || company.ibc_primary}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('labels.lblEmail') || 'E-mail'}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">{company.email}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('labels.lblPhoneLess')}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">
                  +{companyInfo?.telephones?.[0]?.DDI} ({companyInfo?.telephones?.[0]?.DDD}) {companyInfo?.telephones?.[0]?.number}
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-gray-500">{t('others.othOption1')}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">{company.ibc_primary || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('others.othOption2')}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">{company.ibc_secondary || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('others.othOption3')}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">{company.ibc_third || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('labels.lblEmail') || 'E-mail'}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">{company.email}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('labels.lblPhoneLess')}</p>
                <p className="mt-0.5 break-all font-medium text-gray-900">
                  +{companyInfo?.telephones?.[0]?.DDI} ({companyInfo?.telephones?.[0]?.DDD}) {companyInfo?.telephones?.[0]?.number}
                </p>
              </div>
            </>
          )}
        </div>
        {/* Bloco 4: Endereço */}
        {/* <div className="grid grid-cols-2 gap-6 text-sm mb-6">
          <div>
            <p className="text-gray-600">Logradouro</p>
            <p className="font-medium text-black">Rua Georgia</p>
          </div>
          <div>
            <p className="text-gray-600">Número</p>
            <p className="font-medium text-black">465</p>
          </div>
          <div>
            <p className="text-gray-600">Complemento</p>
            <p className="font-medium text-black">Casa 12</p>
          </div>
          <div>
            <p className="text-gray-600">Bairro</p>
            <p className="font-medium text-black">Brooklin Paulista</p>
          </div>
          <div>
            <p className="text-gray-600">Município</p>
            <p className="font-medium text-black">São Paulo</p>
          </div>
          <div>
            <p className="text-gray-600">CEP</p>
            <p className="font-medium text-black">04559-010</p>
          </div>
          <div>
            <p className="text-gray-600">UF</p>
            <p className="font-medium text-black">SP</p>
          </div>
        </div> */}

        {/* Capital */}
        <div className="border-t border-b border-gray-200 py-3 mt-4 mb-4">
          <h2 className="text-base font-bold text-blue-800 mb-1.5">{t('others.othShareCapitalLess')}</h2>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <p className="text-gray-600">{isDelaware ? t('others.othTotalAuthorizedUnits') : t('others.othTotalAuthorizedShares')}</p>
              <p className="font-medium text-black">{company.total_authorized_shares}</p>
            </div>
            <div>
              <p className="text-gray-600">{isDelaware ? t('others.othTotalIssuedUnits') : t('others.othTotalIssuedSharesLess')}</p>
              <p className="font-medium text-black">{company.total_issued_shares}</p>
            </div>
            <div>
              <p className="text-gray-600">{t('others.othNominalValue')}</p>
              <p className="font-medium text-black">${company.par_value}</p>
            </div>
            <div>
              <p className="text-gray-600">{t('companyTransfer.othCurrency')}</p>
              <p className="font-medium text-black">{companyInfo?.currency?.acronym}</p>
            </div>
          </div>
        </div>


        {/* Atividades relevantes */}
        {sicDescriptions && sicDescriptions?.length > 0 && (
          <div className="mt-4">
            <h2 className="text-base font-bold text-blue-800 mb-1.5">{t('others.othRelevantActivities')}</h2>
            <ul className="list-disc ml-6 text-sm text-gray-800">
              {sicDescriptions.map((desc: string | undefined, index: number) => (
                <li key={index}>{desc}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Diretor e Sócio */}
        {/* {primaryShareholder && (
          <div className="border-t pt-4 mt-4">
            <h2 className="text-lg font-bold text-blue-800 mb-2">Diretor Responsável</h2>
            <p className="text-sm"><strong>Nome:</strong> {primaryShareholder.name}</p>
            <p className="text-sm"><strong>Email:</strong> {primaryShareholder.email}</p>
            <p className="text-sm"><strong>Nacionalidade:</strong> {primaryShareholder.nationality}</p>
            <p className="text-sm"><strong>Ocupação:</strong> {primaryShareholder.occupation}</p>
          </div>
        )} */}


        {/* Bloco 1: Dados corporativos */}
        {/* <div className="grid grid-cols-2 gap-6 text-sm mb-6">
          <div>
            <p className="text-gray-600">Nire Matriz</p>
            <p className="font-medium text-black">35233337651</p>
          </div>
          <div>
            <p className="text-gray-600">Data de Constituição</p>
            <p className="font-medium text-black">31/08/2022</p>
          </div>
          <div>
            <p className="text-gray-600">Início de Atividade</p>
            <p className="font-medium text-black">12/01/2011</p>
          </div>
          <div>
            <p className="text-gray-600">Inscrição Estadual</p>
            <p className="font-medium text-black">---</p>
          </div>
        </div>

        {/* Bloco 2: Objeto social */}
        {/* <div className="border-t border-b py-4 my-6">
          <h2 className="text-lg font-bold text-blue-800 mb-2">Objeto Social</h2>
          <ul className="list-disc ml-6 text-sm text-gray-800 leading-relaxed">
            <li>Desenvolvimento de programas de computador sob encomenda</li>
            <li>Consultoria em tecnologia da informação</li>
            <li>Desenvolvimento e licenciamento de programas de computador customizáveis</li>
            <li>Desenvolvimento e licenciamento de programas de computador não-customizáveis</li>
            <li>Portais, provedores de conteúdo e outros serviços de informação na internet</li>
          </ul>
        </div> */}

        {/* Bloco 3: Capital */}
        {/* <div className="grid grid-cols-2 gap-6 text-sm mb-6">
          <div>
            <p className="text-gray-600">Capital</p>
            <p className="font-medium text-black">R$ 87.000,00 (Oitenta e sete mil reais)</p>
          </div>
        </div>   */}



        {/* Bloco 5: Ações pendentes */}
        {/* <div className="border-t pt-4 mt-4">
          <p className="text-red-600 font-medium mb-2">⚠️ Você deve assinar o contrato de usuário antes de finalizar seu cadastro.</p>
          <button
            onClick={() => window.open(documentCompany?.[0]?.url, '_blank')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Assinar contrato
          </button>
        </div> */}

        {/* Bloco 6: Navegar para documentos */}
        {/* <div className="mt-6 text-right">
          <button
            onClick={() => window.location.href = '/documents'} // ou `navigate('/documents')` se estiver usando React Router
            className="px-4 py-2 text-sm bg-gray-200 text-blue-800 border border-blue-600 rounded hover:bg-blue-100"
          >
            Ver documentos
          </button>
        </div>  */}
      </Card.Root>

      <TabsDocuments companyId={companyInfo?.id} />

    </>
  );
};

export { CompanyInfoScreen };
